import React, { useCallback, useEffect, useMemo, useState } from "react";
//redux
import { RootState } from "store/ducks";
import { useDispatch, useSelector } from "react-redux";
//services
import QuoteServices from "services/quote.service";
//assets
import { ConfirmIcon, EditIconTooltip } from "pages/Quotes/assets/imagesSVG";
//utils
import { DisableButton } from "utils/DisableButton";
import { ExistsDuplicatedItem } from "../TRow/utils";
import { IsNullOrEmpty } from "utils/StringNullOrEmpty";
import { RoundDecimalNumber } from "utils/RoundingDecimal";
import { currencyOutputNumber } from "utils/CurrencyOutput";
import { mapSaveSuggestion, userSavedIsAutomatic } from "pages/Quotes/Dashboard/utils";
import { convertFromFloat, convertToFloat } from "pages/Quotes/utils/numberFormatBR";
//models
import { PitagorasProps } from "models/Quote";
import { IQuoteItem, SelectedColorProps, tooltipDefault } from "./interface";
import { IQuoteDetailItemStock } from "models/QuoteDetailItem";
import { StatusType, HistorytSatusDescription } from "components/Table/interface";
import { ProductSegmentDescription, segmentsMinimumPriceLock2 } from "pages/Quotes/components/Tags/interface";
//components
import Text from "components/Text";
import Inputs from "./components/Inputs";
import toast from "components/Toast/dispatch";
import { Visibility } from "components/Visibility";
import BlockedItem from "./components/BlockedItem";
import { Column, Row } from "components/Layout/Grid";
import { DropdownCDs } from "./components/DropdownCDs";
import { AnsweredBionexo } from "./components/AnsweredBionexo";
import ModalLoader from "pages/Quotes/components/generics/Modal/Loader";
import GenericTooltip from "pages/Quotes/components/generics/Tooltip/Tooltip";
import MinPriceModal from "pages/Quotes/components/generics/Tables/Details/components/QuoteItem/components/MinPriceModal";
import MinProfitabilityModal from "pages/Quotes/components/generics/Tables/Details/components/QuoteItem/components/MinProfitabilityModal";
import SupplierObsFieldModal from "pages/Quotes/components/generics/Modal/ObservationFieldModal/SupplierObsFieldModal/SupplierObsFieldModal";
// context
import { useDetailsQuoteContext } from "contexts/DetailsQuote";
import { useConversionFactorContext } from "contexts/ConversionFactor";
import { useExceptionPriceBelowMinContext } from "contexts/ExceptionPriceBelowMin";
//styles
import * as S from "./styles";

const QuoteItem = (props: IQuoteItem) => {
  const {
    item,
    index,
    quoteId,
    cdFilter,
    suggested,
    setChecked,
    customProps,
    customerCode,
    notifyLoading,
    addNewHistory,
    enableButtons,
    setTotalAmount,
    productSelected,
    setSelectedCDItem,
    setProductSelected,
    integrationMessages,
    setSelectedBudgetCd,
    setEditingQuoteItem,
    setExclusionCondition,
    updateConversionAmount,
    addBudgetSaveQuoteItem,
    distributionCenterList,
    segmentsWithoutPriceLock,
    removeBudgetSaveQuoteItem,
    distributionCenterSelected
  } = props;

  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.user);
  const updateBudgetSaved = useSelector((state: RootState) => state.updateBudgetSaved);
  const resetBudget = useSelector((state: RootState) => state.resetBudget);
  const quotesDetailsStore: any = useSelector((state: RootState) => state.quotesDetails);
  const unitPriceValue = useSelector((state: RootState) => state.unitPriceValue);
  const itemSuggestion = item.productsSuggestion
    ?.find((productSuggestion: any) => item.budgets?.sequence ? Number(productSuggestion.sequentialSuggestion) === (item.budgets?.sequence + 1) : productSuggestion.suggested);
  const [stock, setStock] = useState(itemSuggestion?.itemStock.find((productStock: any) => productStock.local === item.distributionCenter));

  const blockedMinimumPrice = useMemo(() => (item?.productSegmentDescription?.toUpperCase() === ProductSegmentDescription.MEDICINE && IsNullOrEmpty(stock?.minimumPriceLock1)), [stock, item]);
  const minimumPrice = useMemo(() => (segmentsMinimumPriceLock2.includes(item?.productSegmentDescription?.toUpperCase()) ? stock?.minimumPriceLock2 : stock?.minimumPriceLock1), [stock, item]);
  const minimumPriceProfitability = useMemo(() => {
    if (Number(stock?.olPrice) > 0)
      return ((stock?.olPrice - (stock?.commercialCost || 0)) / stock?.olPrice) * 100;
    else
      return ((stock?.minimumPriceLock1 - (stock?.commercialCost || 0)) / stock?.minimumPriceLock1) * 100;
  }, [stock]);
  const multiple: number = item?.multiple ? + item?.multiple : 0;
  const accordinglyPrice = useMemo(() => (!stock?.isPricedAccordingly), [stock]);
  const ruleItemNormal = useMemo(() => (stock?.minimumPriceLock1 ? minimumPriceProfitability : 5), [stock, minimumPriceProfitability]);
  const rentabilityValue = useMemo(() => (convertFromFloat(accordinglyPrice ? ruleItemNormal : 0)), [accordinglyPrice, ruleItemNormal]);
  const [subUnidValue, setSubUnidValue] = useState<any>();
  const [isLoading] = useState(false);
  const [modalLoader, setModalLoader] = useState(false);
  const [openItemObsModal, setOpenItemObsModal] = useState(false);
  const [radionPackaging, setRadionPackaging] = useState<number>(item.packagingType);
  const [Notes, setNotes] = useState<string>('');
  const [quantity, setQuantity] = useState<number | null>(item.conversionAmount ?? 0);
  const [openTooltip, setOpenTooltip] = useState(tooltipDefault);
  const [showMinPriceModal, setShowMinPriceModal] = useState(false);
  const [minimumPriceLock, setMinimumPriceLock] = useState<boolean>(false);
  const [updatedMinPrice, setUpdatedMinPrice] = useState<number>(0);
  const [, setMinimumProfitability] = useState<number>();
  const [showMinProfitabilityModal, setShowMinProfitabilityModal] = useState<boolean>(false);
  const [rentability, setRentability] = useState(rentabilityValue);
  const [minProfitabilityMessage, setMinProfitabilityMessage] = useState("");
  const [unitPrice, setUnitPrice] = useState<any>(0);
  const [unitPriceMessage, setUnitPriceMessage] = useState("");
  const [isEditing, setIsEditing] = useState(IsNullOrEmpty(item.budgetSaved));
  const [, setShouldReset] = useState(false);
  const [quantityPackaging, setQuantityPackaging] = useState<number>(suggested?.conversionFactor);
  const [suggestedUnitPrice, setSuggestedUnitPrice] = useState<number>(suggested?.unitPrice);
  const [isKitPackagingActive, setIsKitPackagingActive] = useState<boolean>(false);
  const [isAgreementPriceValue, setIsAgreementPriceValue] = useState(false);
  const [distributionCenter, setDistributionCenter] = useState(item.distributionCenter ?? "");
  const [distributionCenterPitagoras, setDistributionCenterPitagoras] = useState(item.budgets?.distributionCenterPitagoras ?? "");
  const [wasEditing, setWasEditing] = useState(false);
  const [updateUnitPrice, setUpdateUnitPrice] = useState(false);

  const { quoteDetail, quoteItem, isQuoteExpired } = useDetailsQuoteContext();
  const { exceptionPriceBelowMin, updateExceptionPriceBelowMinState } = useExceptionPriceBelowMinContext();
  const { conversionFactor } = useConversionFactorContext();

  const DcArray = useMemo(() =>
    distributionCenterList?.map((cd: any) => cd.value
    ), [distributionCenterList]);

  const bionexoStatus = quoteDetail?.status;
  const viveoStatus = quoteDetail?.supplierStatus;
  const _quoteService = new QuoteServices();
  const segmentComparison = segmentsWithoutPriceLock.includes(item.productSegmentDescription);
  const ableNegotiacionBtn = (isQuoteExpired && IsNullOrEmpty(quoteDetail?.distributionCentersNegotiation?.find((item) => item === distributionCenterSelected)));

  const isOpen = bionexoStatus === StatusType.OPEN;

  const itemOriginal = useMemo(() => (
    quoteItem?.data?.find(x => x.sequenceNumber === item.sequenceNumber && x.isOriginal!)
  ), [quoteItem]);

  const firstBudget = useMemo(() => (
    quoteItem?.data?.find(x => x.sequenceNumber === item.sequenceNumber && x.budgets?.sequence === 0)?.budgets
  ), [quoteItem]);

  const listAllCds = useMemo(() => (
    item.itemStock.filter((itemDc: any) => DcArray?.includes(itemDc.local))
  ), [DcArray, item.itemStock]);

  const listCdsNotDuplicated = useMemo(() => {
    const allCdsFilter = listAllCds.filter((itemDc: any) => DcArray?.includes(itemDc.local) && itemDc.local !== (firstBudget?.distributionCenter ?? itemOriginal?.distributionCenter));

    if (itemOriginal)
      return allCdsFilter;
  }, [listAllCds, itemOriginal]);

  const isItemBlocked = () => {
    const itemStockDetail: IQuoteDetailItemStock = stock;
    return itemStockDetail?.blocked;
  };

  useEffect(() => { setNotes(item.notes); }, [item.notes]);

  useEffect(() => {
    if (stock?.isPricedAccordingly && stock?.agreementPrice > 0)
      unitPriceNoValidator(stock.commercialCost, stock, true);
    else if (stock?.agreementPrice === 0 && Number(stock?.olPrice) > 0)
      unitPriceNoValidator(stock?.olPrice, stock, true);
  }, [stock]);

  const itemBatchess = useMemo(() =>
    suggested?.itemBatch?.find(
      (itemSuggested: any) => itemSuggested.distributionCenter === distributionCenter
    ), [item]);

  useEffect(() => {
    if (resetBudget?.isReset && item.productCode === resetBudget.productCode) {
      setIsEditing(true);
      setEditingQuoteItem(true);
      dispatch({ type: 'SET_RESET_BUDGET', data: { isReset: false, productCode: undefined } });
    }
  }, [resetBudget?.isReset]);

  useEffect(() => {
    setSelectedCDItem(item.distributionCenter || "");
  }, []);

  useEffect(() => {
    if (stock) setMinimumProfitability(stock.minimumProfitability);
  }, [stock]);

  const distributionAvailableMoney = () => {
    const firstDistribution = stockAvailableList[0]?.local;

    if (!firstDistribution || !item?.itemStock) return;

    setDistributionCenter(firstDistribution);
    setSelectedCDItem(firstDistribution);
    setStock(
      item?.itemStock?.find(
        (elem: any) => elem.local === firstDistribution
      )
    );
  };

  useEffect(() => {
    const existDistribution = stockAvailableList?.some((x: IQuoteDetailItemStock) => x.local === item?.distributionCenterDefault) && !IsNullOrEmpty(stockAvailableList);

    if (Number(item.conversionAmount) !== 0 && existDistribution) {
      !IsNullOrEmpty(item.conversionAmount) && setQuantity(item.conversionAmount);
      setStock(
        item?.itemStock?.find(
          (elem: any) => elem.local === item?.distributionCenter
        )
      );
    }

    if (isItemBlocked() || IsNullOrEmpty(item.budgetSaved)) {
      editionMode();
    }

    if (!existDistribution && (item?.distributionCenterDefault === valueSelectDC)) {
      distributionAvailableMoney();
    }
  }, [item, distributionCenterSelected]);

  useEffect(() => {
    setUpdateUnitPrice(false);
    setRentability(convertFromFloat(rentability));
    !IsNullOrEmpty(item.conversionAmount) && setQuantity(item.conversionAmount);
  }, [item.conversionFactor]);

  useEffect(() => {
    if (!isAgreementPriceValue || (!stock?.isPricedAccordingly && !stock?.ol))
      handleKeepFactoryPrice();
  }, [unitPrice]);

  useEffect(() => {
    item.distributionCenter = distributionCenter;
    const newStock = item?.itemStock?.find(
      (elem: any) => elem.local === item?.distributionCenter
    );
    setStock(newStock);

    if (
      newStock?.factoryPrice > 0 &&
      newStock?.commercialCost / (1 - (convertToFloat(rentability) / 100)) > newStock.factoryPrice &&
      newStock.factoryPrice &&
      (!newStock?.isPricedAccordingly && !newStock?.ol)
    ) {
      unitPriceValidator(newStock.factoryPrice === 0 ? minimumPrice : newStock.factoryPrice, newStock);
    } else if (stock?.isPricedAccordingly && stock?.agreementPrice > 0)
      unitPriceNoValidator(stock.commercialCost, newStock, isAgreementPriceValue);
    else if (stock?.agreementPrice === 0 && Number(stock?.olPrice) > 0)
      unitPriceNoValidator(stock?.olPrice, newStock, isAgreementPriceValue);

    setSelectedBudgetCd(distributionCenter);
  }, [distributionCenter, isAgreementPriceValue]);

  useEffect(() => {
    handleTotalAmount();
    let value = unitPrice * Number(quantity);
    let subUnidValue;

    if (conversionFactor && !isNaN(conversionFactor) && conversionFactor > 0) {
      if (isEditing)
        subUnidValue = unitPrice / conversionFactor;
      else
        subUnidValue = item.salePrice / conversionFactor;
    }
    else subUnidValue = "-";

    if (!isNaN(value) && stock) {
      if (!stock?.isPricedAccordingly) {
        if (unitPrice > stock?.factoryPrice && stock.factoryPrice !== 0) {
          setUnitPriceMessage(
            `Acima do preço de \nfábrica (${currencyOutputNumber(stock.factoryPrice)})`
          );
          setOpenTooltip({
            ...openTooltip,
            ...{ unitPrice: false, unitPriceValue: stock.factoryPrice },
          });
        } else {
          setOpenTooltip({ ...openTooltip, ...{ unitPrice: false } });
        }

        handleMinPriceLock();
      }
      setSubUnidValue(subUnidValue);
      setSuggestedUnitPrice(Number(subUnidValue));
    }
  }, [unitPrice, rentability, conversionFactor, quantity, stock, isEditing]);

  const totalValue = useMemo(() => {
    if (isEditing)
      return unitPrice * Number(quantity);
    else
      return item.salePrice * Number(quantity);
  }, [isEditing, item.budgetSaved, unitPrice, quantity]);

  useEffect(() => {
    validateFactoryPrice();
  }, [rentability, stock]);

  useEffect(() => {
    if (unitPriceValue?.unitPrice && unitPriceValue?.item && unitPriceValue?.item.id === item.id) {
      unitPriceValidator(unitPriceValue.unitPrice, unitPriceValue.item.commercialCost);
      dispatch({ type: "REMOVE_UNIT_PRICE_VALUE" });
    }
  }, [unitPriceValue]);

  useEffect(() => {
    updateConversionAmount(quantity ?? 0);
  }, [quantity]);

  useEffect(() => {
    setIsEditing(IsNullOrEmpty(item.budgetSaved));
    const cdsNotDuplicated = listCdsNotDuplicated?.map((m: any) => m.local);

    if ((!IsNullOrEmpty(item.distributionCenter) && ((!item.budgets || item.budgets?.sequence === 0) && !IsNullOrEmpty(item.budgetSaved))) || (item.budgets?.sequence === 1 && cdsNotDuplicated?.includes(item.distributionCenter)))
      setDistributionCenter(item.distributionCenter);
    else if (listCdsNotDuplicated && !IsNullOrEmpty(item.budgets) && item.budgets?.sequence === 1) {
      setDistributionCenter(listCdsNotDuplicated[0]?.local);
      setStock(item.productsSuggestion
        ?.find((productSuggestion: any) => item.budgets?.sequence ? Number(productSuggestion.sequentialSuggestion) === (item.budgets?.sequence + 1) : productSuggestion.suggested)
        ?.itemStock.find((productStock: any) => productStock.local === listCdsNotDuplicated[0]?.local));
    }
  }, [item.budgetSaved, item.budgets]);

  useEffect(() => {
    notifyLoading(isLoading);
  }, [isLoading]);

  const handleExclusionCondition = () => {
    if (setExclusionCondition) {
      const sentBionexo = item?.historyBudgets?.find((x: any) => x.sentBionexo);
      const { supplierProductcode: itemCode } = item.maps || item;
      const { supplierProductCode: sentCode } = sentBionexo || {};
      const isDifferentSupplierCode = itemCode !== sentCode;

      setExclusionCondition(isDifferentSupplierCode);
    }
  };

  useEffect(() => {
    handleExclusionCondition();
  }, [item?.supplierProductCode, isEditing]);

  const itemWithoutOlAgreement = !stock?.isPricedAccordingly && !stock?.ol && Number(stock?.olPrice) === 0;
  const isUnitPriceValid = IsNullOrEmpty(unitPrice) ? true : Number(unitPrice?.toFixed(2)) < Number(minimumPrice?.toFixed(2)) || Number(unitPrice?.toFixed(2)) < updatedMinPrice;
  const suggestedItem = item.productsSuggestion?.find((product: any) => product.suggested == true);
  const valueMultiple = useMemo(() => (item?.priorityBCG?.maps?.find((map: any) => map.codProdMafra === suggestedItem?.supplierProductcode)?.multiple ?? 1), [item]);
  const valuePricing = Number((minimumPrice * valueMultiple).toFixed(2));

  const suggestedIsCreatedAutomatically = suggested?.isCreatedAutomatically;
  const isNotAbleToEditWhenAutomatically = suggestedIsCreatedAutomatically && !suggested.isApproved;

  const handleMinPriceLock = () => {
    if ((isUnitPriceValid || unitPrice < valuePricing) && !blockedMinimumPrice && minimumPrice > 0) {
      setMinimumPriceLock(true);
      const unitPriceFixed = Number((unitPrice).toFixed(2));

      if (!IsNullOrEmpty(valuePricing) && unitPriceFixed < valuePricing && itemWithoutOlAgreement) {
        setUnitPriceMessage(`Aplicar preço pricing ${currencyOutputNumber(valuePricing)}`);

        if (unitPriceFixed >= minimumPrice)
          setMinimumPriceLock(false);
        else
          setOpenTooltip({
            ...openTooltip,
            ...{ unitPrice: true, unitPriceValue: valuePricing }
          });
      } else if (IsNullOrEmpty(unitPrice) || updatedMinPrice === 0 && (Number(stock?.agreementPrice) === 0 && Number(stock?.olPrice) > 0 && Number(unitPrice?.toFixed(2)) < Number(stock?.olPrice))) {
        setUnitPriceMessage(`Aplicar valor de OL ${currencyOutputNumber(Number(stock?.olPrice))}`);
        setOpenTooltip({
          ...openTooltip,
          ...{ unitPrice: true, unitPriceValue: stock?.olPrice }
        });
      } else if (IsNullOrEmpty(unitPrice) ||
        (updatedMinPrice === 0 && (Number(unitPrice.toFixed(2)) < Number(minimumPrice.toFixed(2)) && Number(stock?.olPrice) === 0)) ||
        (!blockedMinimumPrice && Number(stock?.olPrice) === 0)) {
        if (!user?.isBCGUser)
          setUnitPriceMessage(`Aplicar valor mínimo ${currencyOutputNumber(minimumPrice)}`);

        setOpenTooltip({
          ...openTooltip,
          ...{ unitPrice: true, unitPriceValue: minimumPrice }
        });
      } else if (updatedMinPrice > 0 && unitPrice < updatedMinPrice && Number(stock?.olPrice) === 0) {
        if (!user?.isBCGUser)
          setUnitPriceMessage(`Aplicar valor mínimo ${currencyOutputNumber(updatedMinPrice)}`);

        setOpenTooltip({
          ...openTooltip,
          ...{ unitPrice: true, unitPriceValue: updatedMinPrice },
        });
      }
    } else {
      setMinimumPriceLock(false);
    }
  };

  const handleCloseItemObsModal = () => {
    setOpenItemObsModal(false);
  };

  const validateFactoryPrice = () => {
    let valueAsFloat = convertToFloat(rentability);

    if (stock?.commercialCost && !IsNullOrEmpty(valueAsFloat) && !updateUnitPrice) {
      let value = 0;
      
      const hasValueMultipleBCG = user?.isBCGUser && item.priorityBCG && valueMultiple;
      const hasOlOrAgreementPrice = stock?.agreementPrice === 0 && stock?.olPrice === 0;
      const applyValuePricing = hasValueMultipleBCG && hasOlOrAgreementPrice;

      if (applyValuePricing)
        value = valuePricing;
      else
        value = stock?.commercialCost / (1 - (valueAsFloat / 100));


      if (!isNaN(value)) {
        if (applyValuePricing) {
          const rentability = value !== 0 ? ((valuePricing - stock?.commercialCost) / valuePricing) * 100 : 0;

          setRentability(convertFromFloat(rentability));
        }

        setUnitPrice(RoundDecimalNumber(Number(value), 2));
      }
    }
  };

  const listCemed = stock?.factoryPrice > 0;
  const rentabilityInvalid = rentability === "-" || rentability === "";

  const unitPriceValidator = useCallback((unitPriceParamValidator: any, stockValidator: any) => {
    const unitPriceValid = unitPriceParamValidator !== 0 && !IsNullOrEmpty(unitPriceParamValidator);
    const commercialCost = stockValidator?.commercialCost || 0;
    const newRentability = unitPriceValid ? ((unitPriceParamValidator - commercialCost) / unitPriceParamValidator) * 100 : 0;

    if (commercialCost) {
      setUpdateUnitPrice(true);
      setRentability(convertFromFloat(newRentability));
    }

    if (listCemed && convertFromFloat(unitPriceParamValidator) > stockValidator?.factoryPrice && stockValidator?.factoryPrice > 0) {
      setUnitPrice(RoundDecimalNumber(Number(stockValidator?.factoryPrice), 2));
    } else {
      const result = stockValidator?.commercialCost / (1 - (newRentability / 100));

      if (listCemed && minimumPrice > stockValidator?.factoryPrice && stockValidator?.factoryPrice > 0) {
        setRentability(convertFromFloat(newRentability));
        setUnitPrice(RoundDecimalNumber(Number(stockValidator?.factoryPrice), 2));
      } else {
        if (unitPriceValid)
          setUnitPrice(RoundDecimalNumber(Number(result), 2));
        else
          setUnitPrice(RoundDecimalNumber(Number(unitPriceParamValidator), 2));
      }
    }
  }, [rentability]);

  const unitPriceNoValidator = useCallback((unitPriceParamNoValidator: any, stockNoValidator: any, executeFunction?: boolean) => {
    if (isAgreementPriceValue || executeFunction) {
      const unitPriceValid = unitPriceParamNoValidator !== 0 && !IsNullOrEmpty(unitPriceParamNoValidator);
      const commercialCost = stockNoValidator?.commercialCost || 0;
      const newRentability = unitPriceValid ? ((unitPriceParamNoValidator - commercialCost) / unitPriceParamNoValidator) * 100 : 0;
      const result = stockNoValidator?.commercialCost / (1 - (newRentability / 100));

      setUpdateUnitPrice(true);
      setRentability(convertFromFloat(newRentability));

      if (unitPriceValid)
        setUnitPrice(RoundDecimalNumber(Number(result), 2));
      else
        setUnitPrice(RoundDecimalNumber(Number(unitPriceParamNoValidator), 2));
    }
  }, [rentability]);

  const validateConversionQt = (
    quantity: string,
    multiple: number,
    forceSetRoundedNumber?: boolean
  ): number | null => {
    if (quantity && quantity !== "undefined" && quantity !== "-") {
      const quantityNumber: number = Number(quantity);

      if (quantityNumber !== 0) {
        const remainder = quantityNumber < multiple ? 0 : quantityNumber % multiple;

        if (remainder !== 0) {
          const number = quantityNumber - remainder;
          if (
            forceSetRoundedNumber != undefined &&
            forceSetRoundedNumber === true
          ) {
            setOpenTooltip({ ...openTooltip, ...{ quantity: false } });
            return number;
          }
        } else {
          setOpenTooltip({ ...openTooltip, ...{ quantity: false } });
          return quantityNumber;
        }
      } else {
        setOpenTooltip({ ...openTooltip, ...{ quantity: true } });
        return quantityNumber;
      }
      return null;
    }

    return 0;
  };

  const handleTotalAmount = () => {
    if (
      conversionFactor &&
      Number(quantity) !== 0 &&
      Number(quantity) !== null &&
      Number(quantity) !== undefined
    ) {
      const total = Number(quantity) * conversionFactor;
      setTotalAmount(Math.round(total));
    } else {
      setTotalAmount(0);
    }
  };

  const saveBudget = async (conversionAmount: number | string) => {
    setModalLoader(true);
    const name = user?.fullName ?? '';
    const email = user?.email ?? '';
    const isValuePricing = valuePricing === unitPrice;
    const pitagorasSelected = cdsPitagoras.find(cd => cd.local === distributionCenterPitagoras);

    const newBudget = {
      name,
      email,
      quoteId,
      isValuePricing,
      conversionAmount,
      itemStock: stock,
      salePrice: unitPrice,
      subUnit: item?.subUnit,
      crmCustomerCode: customerCode,
      outOfStock: stock?.stock <= 0,
      totalValue: String(totalValue),
      quotationMultiple: item.multiple,
      factoryPrice: stock?.factoryPrice,
      sequenceNumber: item?.sequenceNumber,
      status: item?.historyBudgets?.status,
      productSegment: item?.productSegment,
      distributionCenterStock: stock.stock,
      sequenceBudget: item?.budgets?.sequence,
      pitagorasStock: pitagorasSelected!.stock,
      quotationQuantityPackaging: radionPackaging,
      distributionCenter: distributionCenter ?? "",
      rentability: convertToFloat(rentability || 0),
      pitagorasPosition: pitagorasSelected!.position,
      isPricedAccordingly: stock?.isPricedAccordingly,
      statusDescription: HistorytSatusDescription.SAVED,
      supplierProductCodeRA: item?.supplierProductCodeRA,
      pitagorasDistributionCenter: pitagorasSelected!.local,
      acceptPriceBelowMin: exceptionPriceBelowMin.isException,
      note: isValuePricing ? "Preço praticado pelo pricing" : exceptionPriceBelowMin.reason,
      multipleBCG: item?.priorityBCG?.maps?.find((elem: any) => elem.codProdMafra === item?.supplierProductCode)?.multiple,
      unitPrice: IsNullOrEmpty(subUnidValue) || subUnidValue === '-' ? 0 : subUnidValue,
      supplierProductCode: item?.budgets?.name === "Resposta Automática" ? item?.supplierProductCodeRA : item?.supplierProductcode,
      suggested: mapSaveSuggestion(suggested, conversionAmount, unitPrice, rentability, item.quantity, distributionCenter, item.competitorPrice, conversionFactor, subUnidValue)
    };

    const cdSelected = distributionCenterSelected === "default" ? item.distributionCenter : distributionCenter;
    const response = await new QuoteServices().saveBudget(newBudget);

    if (response.status === 200) {
      setProductSelected([...productSelected, { distributionCenter: cdSelected, sequenceNumber: item.sequenceNumber, tabSelected: distributionCenterSelected ?? "", sequence: item.budgets ? item.budgets?.sequence : 0 }]);
      setModalLoader(false);
      setWasEditing(false);

      setEditingQuoteItem(false);
      addNewHistory(newBudget);
      setChecked(true);
      dispatch({ type: 'SET_NEW_BUDGET_SAVED', data: { isUpdate: !updateBudgetSaved?.isUpdate } });
      setIsEditing(false);
      addBudgetSaveQuoteItem(newBudget);
    } else {
      setIsEditing(true);
      setWasEditing(true);
      setModalLoader(false);
      toast.error(response.data.data);
    }
  };

  const getProductStatus = async (
    distributionCenter: string,
    productCode: string,
    message: string,
    customerCode: string
  ) => {
    const result = await new QuoteServices().productStatusUpdate({
      distributionCenter,
      productCode,
      message,
      customerCode,
    });
    return result.data;
  };

  const editionMode = async (isResetBudget?: boolean) => {
    setWasEditing(true);

    if (isResetBudget) {
      setIsEditing(true);
      setEditingQuoteItem(true);
      let newItens = [...productSelected];
      const index = newItens.findIndex(x => x.sequenceNumber === item.sequenceNumber);

      if (index !== -1) {
        newItens.splice(index, 1);
        setProductSelected(newItens);
        setChecked(false);
      }

      removeBudgetSaveQuoteItem();

      const sequenceNumber = item.sequenceNumber;
      await _quoteService.resetBuget({ quoteId, sequenceNumber, sequenceBudget: item.budgets?.sequence ?? 0 });
      dispatch({ type: 'SET_NEW_BUDGET_SAVED', data: { isUpdate: !updateBudgetSaved?.isUpdate } });
    }
  };

  const validateConversionAmount = () => {
    let conversionAmount = validateConversionQt(String(quantity), multiple, true);
    if (conversionAmount == null || conversionAmount == 0) return false;

    if (quantity == null || quantity == 0 || !stock ||
      (stock && ((Number(unitPrice.toFixed(2)) - Number(stock.factoryPrice.toFixed(2))) > 0.0001) && stock.factoryPrice !== 0 && (accordinglyPrice)))
      return false;

    return conversionAmount;
  };

  const saveQuoteItem = () => {
    const compareValue = !IsNullOrEmpty(unitPrice) && Number(unitPrice.toFixed(2)) > Number(stock?.factoryPrice.toFixed(2));

    let conversionAmount = validateConversionAmount();
    if (!conversionAmount) return;
    if (rentabilityInvalid) return;
    if (!unitPrice) return;
    if (listCemed && compareValue && (accordinglyPrice)) return;
    if ((openTooltip.unitPrice && !exceptionPriceBelowMin.isException) && (!blockedMinimumPrice && !segmentComparison) && !stock?.ol) return;
    if (wasEditing) {
      if (!rentability) {
        setUpdateUnitPrice(false);
        setRentability(0);
      }
      saveBudget(conversionAmount);
      updateExceptionPriceBelowMinState(false);
    }
  };

  const setSelectedDc = (selectedCd: string) => {
    setIsAgreementPriceValue(true);
    setDistributionCenter(selectedCd);
    setSelectedCDItem(selectedCd);
  };

  const setSelectedPitagoras = (selectedCdPitagoras: string) => {
    setDistributionCenterPitagoras(selectedCdPitagoras);
  };

  const convertedRentabilityValue: string = useMemo(() => {
    let convertedRentability = rentability === "-"
      ? rentability
      : Number(rentability.toString().replace(",", ".")).toLocaleString(
        "pt-BR",
        { maximumFractionDigits: 4 }
      );

    if (convertedRentability === "-0") {
      convertedRentability = "0";
    }
    return convertedRentability;
  }, [rentability]);

  const handleKeepFactoryPrice = () => {
    if (Math.abs(unitPrice - stock?.factoryPrice) < 0.0001 && (!stock?.isPricedAccordingly && !stock?.ol && Number(stock?.olPrice) === 0))
      setUnitPrice(RoundDecimalNumber(Number(stock?.factoryPrice), 2));

    if ((unitPrice > stock?.factoryPrice && stock?.factoryPrice >= 0)) {
      if (stock?.isPricedAccordingly && stock?.agreementPrice > 0) {
        unitPriceNoValidator(stock.commercialCost, stock);
      } else if (stock?.agreementPrice === 0 && Number(stock?.olPrice) > 0) {
        unitPriceNoValidator(stock?.olPrice, stock);
      } else {
        unitPriceValidator(stock.factoryPrice === 0 ? unitPrice : stock.factoryPrice, stock);
      }
    }

    if (unitPrice < 0 && (!stock?.isPricedAccordingly && !stock?.ol && Number(stock?.olPrice) === 0)) {
      unitPriceValidator(0, stock);
    }
  };

  const lengthQuoteItem = quoteItem?.data?.filter(i => !i.budgets?.deleted).length;

  const cdSelected = item.budgets?.sequence === 0 ? distributionCenterSelected : distributionCenter;
  const tabSelected = !!(distributionCenterSelected && distributionCenterSelected !== 'default');
  const valueSelectDC = tabSelected ? cdSelected : distributionCenter;
  const valueSelectDCPitagoras = distributionCenterPitagoras;
  const disabledSelectDC = (tabSelected && item.budgets?.sequence === 0) || (tabSelected && item.budgets?.sequence === 1 && item.budgets?.sent);

  const cdsPitagoras = useMemo(() => {
    const cdSelectedToPitagoras = item.itemStock.find((itemDc: any) => itemDc.local === valueSelectDC);
    const pitagoras: PitagorasProps[] = cdSelectedToPitagoras?.pitagoras;
    const items: SelectedColorProps[] = [];

    if (!IsNullOrEmpty(pitagoras)) {
      pitagoras.map((p: PitagorasProps) => {
        items.push({
          local: p.distributionCenter,
          stock: p.stock,
          position: p.position
        });
      });
    } else if (!IsNullOrEmpty(cdSelectedToPitagoras)) {
      items.push({
        local: cdSelectedToPitagoras.local,
        stock: cdSelectedToPitagoras.stock,
        position: 0
      });
    }

    const indexOldPitagoras = pitagoras?.findIndex((p: PitagorasProps) => p.distributionCenter === distributionCenterPitagoras);
    if ((IsNullOrEmpty(distributionCenterPitagoras) && items.length > 0) ||
      (IsNullOrEmpty(pitagoras) && items.length > 0) ||
      (indexOldPitagoras === -1 && items.length > 0 && isEditing)) {
      setSelectedPitagoras(items[0].local);
    }

    return items;
  }, [valueSelectDC]);

  const saveOrToastAgreementPrice = () => {
    stock?.isPricedAccordingly && stock?.agreementPrice !== unitPrice ? toastAgreementPrice() : saveQuoteItem();
  };

  const toastAgreementPrice = () => {
    toast.yesNoCustom(
      'Atenção',
      'O Preço de Venda esta diferente do Preço de Acordo, deseja continuar mesmo assim?',
      () => {
        dispatch({ type: "CLOSE_TOAST" });
        saveQuoteItem();
      },
      undefined,
      undefined,
      'Sim',
      'Cancelar'
    );
  };

  const blockedPendingMoney = (integrationMessages?.statusViveo === "Pendente Money" && cdFilter?.distributionCenter !== 'default');
  const nameUserSavedBudget = `<p class='text-styles text-center'>Item salvo por:<br /><b class='color-blue-gray fs-2'>${item?.budgetSaved?.name || user?.fullName}</b></p>`;
  const isOrder = bionexoStatus === StatusType.PROCESSING || bionexoStatus === StatusType.PURCHASE_ORDER || !enableButtons || blockedPendingMoney || ExistsDuplicatedItem(item, quoteItem);
  const blockedDc = distributionCenter === "LAJ" || ableNegotiacionBtn || (!conversionFactor && !stock?.blocked);
  const blockedDcMessage = blockedDc ? "Não é possível salvar orçamentos com CD de Lajeado." : "";
  const pendingMoneyCondition = blockedPendingMoney;
  const showTipContent = pendingMoneyCondition || !enableButtons ? 'Item não pode ser editado' : nameUserSavedBudget;
  const tipContent = () => {
    if (isNotAbleToEditWhenAutomatically) {
      return "Item deve ser aprovado para salvar a cotação.";
    }
    if (ableNegotiacionBtn)
      return "CD não liberado para negociação";
    if (!conversionFactor)
      return "Adicione o fator de conversão para salvar o item";

    return blockedDcMessage;
  };

  const tipInput = () => {
    if (isNotAbleToEditWhenAutomatically) return "Item deve ser aprovado para editar a cotação.";

    return "";
  };

  const confirmOrEditIcon = isEditing ? (
    <ConfirmIcon
      showTip={blockedDc || isNotAbleToEditWhenAutomatically}
      tipContent={tipContent()}
      className={`consfirmBtn ${DisableButton(isOrder)} ${DisableButton(blockedDc)}`}
      dataTestid="confirmIcon"
      disabled={quantity === null || isOrder || pendingMoneyCondition || ableNegotiacionBtn || !conversionFactor || isNotAbleToEditWhenAutomatically}
      onClick={() => !blockedDc && !isOrder && saveOrToastAgreementPrice()}
    />
  ) : (
    <EditIconTooltip
      showTip
      disabled={isOrder || pendingMoneyCondition}
      onClick={() => editionMode(true)}
      showTipContent={showTipContent}
    />
  );

  const renderListCds = (IsNullOrEmpty(item.budgets) || item.budgets?.sequence === 0 || distributionCenterSelected === 'default' || (item.budgets && !item.budgets.sequence));
  const stockAvailableList: IQuoteDetailItemStock[] = renderListCds ? listAllCds : listCdsNotDuplicated;

  return (
    <S.QuoteItem>
      <Column>
        <Row>
          {modalLoader && <ModalLoader />}
          <div className="btnArea">
            {(stock?.blocked || itemSuggestion?.isBlockedItem) && isEditing ? (
              <BlockedItem
                stock={stock}
                item={item}
                customProps={customProps}
                setShouldReset={setShouldReset}
                getProductStatus={getProductStatus}
                distributionCenter={distributionCenter}
                quotesDetailsStore={quotesDetailsStore}
                disabled={isOrder || blockedDc}
              />
            ) : (
              confirmOrEditIcon
            )}
          </div>

          <DropdownCDs
            index={index}
            isEditing={isEditing}
            cdsPitagoras={cdsPitagoras}
            valueSelectDC={valueSelectDC}
            setSelectedDc={setSelectedDc}
            lengthQuoteItem={lengthQuoteItem ?? 0}
            stockAvailableList={stockAvailableList}
            distributionCenter={distributionCenter}
            setSelectedPitagoras={setSelectedPitagoras}
            valueSelectDCPitagoras={valueSelectDCPitagoras}
            distributionCenterPitagoras={distributionCenterPitagoras}
            disabled={isOrder || disabledSelectDC || ableNegotiacionBtn}
          />

          <Inputs
            item={item}
            stock={stock}
            quantity={quantity}
            multiple={multiple}
            unitPrice={unitPrice}
            isEditing={isEditing}
            listCemed={listCemed}
            setQuantity={setQuantity}
            openTooltip={openTooltip}
            minimumPrice={minimumPrice}
            setUnitPrice={setUnitPrice}
            setOpenTooltip={setOpenTooltip}
            setRentability={setRentability}
            updatedMinPrice={updatedMinPrice}
            unitPriceMessage={unitPriceMessage}
            minimumPriceLock={minimumPriceLock}
            showMinPriceModal={showMinPriceModal}
            segmentComparison={segmentComparison}
            setUpdateUnitPrice={setUpdateUnitPrice}
            readOnly={isOrder || ableNegotiacionBtn || isNotAbleToEditWhenAutomatically}
            setMinimumPriceLock={setMinimumPriceLock}
            blockedMinimumPrice={blockedMinimumPrice}
            setShowMinPriceModal={setShowMinPriceModal}
            validateConversionQt={validateConversionQt}
            handleKeepFactoryPrice={handleKeepFactoryPrice}
            unitPriceValidator={stock?.isPricedAccordingly || Number(stock?.olPrice) > 0 ?
              unitPriceNoValidator : unitPriceValidator
            }
            minProfitabilityMessage={minProfitabilityMessage}
            setIsAgreementPriceValue={setIsAgreementPriceValue}
            convertedRentabilityValue={convertedRentabilityValue}
            tooltipText={tipInput()}
          />

          <div className="totalValue">
            <span className="subtitle">Valor Total</span>
            <div className="value">
              {currencyOutputNumber(totalValue)}
            </div>
          </div>

          <div className="answeredBionexo">
            <AnsweredBionexo
              item={item}
              notes={Notes}
              isEditing={isEditing}
              radionPackaging={radionPackaging}
              quantityPackaging={quantityPackaging}
              setRadionPackaging={setRadionPackaging}
              setOpenItemObsModal={setOpenItemObsModal}
              setQuantityPackaging={setQuantityPackaging}
              isKitPackagingActive={isKitPackagingActive}
              isOrderStatus={isOrder || ableNegotiacionBtn}
              setIsKitPackagingActive={setIsKitPackagingActive}
              formatedSubUnidValue={currencyOutputNumber(subUnidValue)}
              unitPrice = {currencyOutputNumber(item.budgets?.maps?.unitPrice || subUnidValue)}
            />
          </div>
        </Row>

        <Visibility
          visible={item?.messageAutoAnswer?.length > 0 &&
            isEditing && item?.historyBudgets.length === 0 &&
            (item?.budgets?.sequence === 0 || IsNullOrEmpty(item?.budgets?.sequence))}
        >
          <Text color="#BEBEBE" size={12} className="messageAutoAnswer">
            <span>RA: </span>
            {item?.messageAutoAnswer?.join("; ")}
          </Text>
        </Visibility>

        <Visibility visible={openItemObsModal}>
          <GenericTooltip
            areaRight="2%"
            areaBottom="70%"
            disable
            width={"534px"}
            maxWidth={"534px"}
            height={"200px"}
            padding={"10px"}
            overflow={"hidden"}
          >
            <SupplierObsFieldModal
              item={{ ...item, quoteId, Notes }}
              handleCloseItemObsModal={handleCloseItemObsModal}
              sequenceNumber={item.sequenceNumber}
              setNotes={setNotes}
            />
          </GenericTooltip>
        </Visibility>
      </Column>

      <Visibility visible={openItemObsModal}>
        <GenericTooltip
          areaRight="2%"
          areaBottom="70%"
          disable
          width={"534px"}
          maxWidth={"534px"}
          height={"200px"}
          padding={"10px"}
          overflow={"hidden"}
        >
          <SupplierObsFieldModal
            item={{ ...item, quoteId, Notes }}
            handleCloseItemObsModal={handleCloseItemObsModal}
            sequenceNumber={item.sequenceNumber}
            setNotes={setNotes}
          />
        </GenericTooltip>
      </Visibility>

      <Visibility visible={isEditing && openTooltip.unitPrice && showMinPriceModal}>
        <GenericTooltip
          areaRight="58.5%"
          areaBottom="-140%"
          disable
          width={"353px"}
          maxWidth={"534px"}
          height={"419px"}
          maxHeight={"419px"}
          padding={"0"}
        >
          <MinPriceModal
            open={showMinPriceModal}
            setOpen={setShowMinPriceModal}
            supplierProductcode={item.supplierProductcode}
            distributionCenter={distributionCenter}
            customerCode={customerCode}
            unitPrice={unitPrice}
            setUpdatedMinPrice={setUpdatedMinPrice}
            stock={stock}
            sequenceNumber={item.sequenceNumber}
            setOpenTooltip={setOpenTooltip}
            setUnitPriceMessage={setUnitPriceMessage}
          ></MinPriceModal>
        </GenericTooltip>
      </Visibility>

      <Visibility visible={isEditing && showMinProfitabilityModal}>
        <GenericTooltip
          areaRight="69%"
          areaBottom="12%"
          disable
          width={"260px"}
          maxWidth={"534px"}
          height={"260px"}
          padding={"0"}
          overflow={"hidden"}
        >
          <MinProfitabilityModal
            open={showMinProfitabilityModal}
            setOpen={setShowMinProfitabilityModal}
            supplierProductcode={item.supplierProductcode}
            customerCode={customerCode}
            distributionCenter={distributionCenter}
            setMinimumProfitability={setMinimumProfitability}
            setMinProfitabilityMessage={setMinProfitabilityMessage}
          ></MinProfitabilityModal>
        </GenericTooltip>
      </Visibility>
    </S.QuoteItem>
  );
};

export default QuoteItem;
